import React, { useState, useEffect } from 'react';
import './CookieConsent.css';

// Cookie utility functions
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};


// Global function to open cookie settings (can be called from anywhere)
window.openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent('openCookieSettings'));
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    preferences: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const consentCookie = getCookie('mycinemas_cookie_consent');
    if (!consentCookie) {
      // First-time visitor - show banner
      setShowBanner(true);
    } else {
      // Load saved consent preferences
      try {
        const savedConsent = JSON.parse(consentCookie);
        setConsent(savedConsent);
      } catch (error) {
        console.error('Error parsing consent cookie:', error);
        setShowBanner(true); // Show banner if cookie is invalid
      }
    }

    // Listen for open cookie settings event
    const handleOpenSettings = () => {
      setShowSettings(true);
      setShowBanner(false);
    };

    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => {
      window.removeEventListener('openCookieSettings', handleOpenSettings);
    };
  }, []);

  const handleAccept = () => {
    // Accept all cookies
    const allConsent = {
      necessary: true,
      analytics: true,
      preferences: true,
      marketing: true,
    };
    saveConsent(allConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    saveConsent(consent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveConsent = (consentData) => {
    // Save consent in cookie
    setCookie('mycinemas_cookie_consent', JSON.stringify(consentData), 365);
    setConsent(consentData);
    
    // If analytics is not allowed, clear analytics-related data
    if (!consentData.analytics) {
      // Clear any analytics cookies/localStorage if needed
      // console.log('Analytics disabled - clearing analytics data');
    }
    
    // If preferences is not allowed, clear preference data
    if (!consentData.preferences) {
      // Clear theme preference
      localStorage.removeItem('theme');
      // console.log('Preferences disabled - clearing preference data');
    }
    
    // If marketing is not allowed, clear marketing data
    if (!consentData.marketing) {
      // Clear any marketing-related data
      console.log('Marketing disabled - clearing marketing data');
    }
    
    // Trigger custom event for other components to listen
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consentData }));
  };

  const handleRejectAll = () => {
    // Only allow necessary cookies
    const minimalConsent = {
      necessary: true,
      analytics: false,
      preferences: false,
      marketing: false,
    };
    saveConsent(minimalConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner && !showSettings) {
    return null;
  }

  return (
    <>
      {/* Cookie Consent Banner */}
      {showBanner && !showSettings && (
        <div className="cookie-consent-banner">
          <div className="cookie-consent-content">
            <div className="cookie-consent-text">
              <h4>We value your privacy</h4>
              <p>
                My Cinemas uses cookies to improve your browsing experience, analyse usage patterns, 
                and provide personalized services. By continuing to use our website or mobile apps, 
                you consent to the use of cookies in accordance with our Privacy Policy.
              </p>
              <p className="cookie-consent-subtext">
                You can manage or disable cookies anytime through your browser or device settings.
              </p>
            </div>
            <div className="cookie-consent-actions">
              <button 
                className="cookie-btn cookie-btn-secondary" 
                onClick={() => {
                  setShowBanner(false);
                  setShowSettings(true);
                }}
              >
                Manage Settings
              </button>
              <button 
                className="cookie-btn cookie-btn-primary" 
                onClick={handleAccept}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="cookie-settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="cookie-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cookie-settings-header">
              <h3>Cookie Settings</h3>
              <button 
                className="cookie-settings-close" 
                onClick={() => {
                  setShowSettings(false);
                  if (!getCookie('mycinemas_cookie_consent')) {
                    setShowBanner(true);
                  }
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="cookie-settings-content">
              <p className="cookie-settings-intro">
                {/* ORIGINAL: We use cookies to enhance your experience. You can choose which types of cookies to accept. */}
                We use cookies to make the site work properly, understand how you use it, and remember your preferences. You can choose which categories to allow. Necessary cookies are always active.
              </p>

              {/* Necessary Cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>Necessary Cookies</h4>
                  {/* ORIGINAL: Essential for the website to function properly. These cannot be disabled. */}
                  <p>These cookies are essential for the site to work. They enable core features like secure ticket booking, remembering your selected city, and keeping your booking details safe while you complete a purchase. They cannot be turned off.</p>
                </div>
                {/* hidden-toggle-backup:
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    readOnly
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
                */}
                <span className="always-active-badge">Always Active</span>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>Analytics Cookies</h4>
                  {/* ORIGINAL: Help us understand how visitors interact with our website by collecting and reporting information anonymously. */}
                  <p>These cookies help us understand how visitors use our website — for example, which pages are visited most often. This helps us improve your experience. These are optional.</p>
                </div>
                <label className="cookie-toggle">
                  <input 
                    type="checkbox" 
                    checked={consent.analytics}
                    onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
              </div>

              {/* Preferences Cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>Preferences Cookies</h4>
                  {/* ORIGINAL: Remember your preferences (like theme, language, location) to provide a personalized experience. */}
                  <p>These cookies remember your choices, like theme and language, so you don't have to set them again every time you visit. These are optional.</p>
                </div>
                <label className="cookie-toggle">
                  <input 
                    type="checkbox" 
                    checked={consent.preferences}
                    onChange={(e) => setConsent({ ...consent, preferences: e.target.checked })}
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
              </div>

              {/* Marketing Cookies */}
              <div className="cookie-setting-item">
                <div className="cookie-setting-info">
                  <h4>Marketing Cookies</h4>
                  {/* ORIGINAL: Used to deliver relevant advertisements and track campaign performance. */}
                  <p>These cookies are used to show you ads that are relevant to you and to measure how well our marketing campaigns are working. These are optional.</p>
                </div>
                <label className="cookie-toggle">
                  <input 
                    type="checkbox" 
                    checked={consent.marketing}
                    onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="cookie-settings-actions">
              <button 
                className="cookie-btn cookie-btn-secondary" 
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button 
                className="cookie-btn cookie-btn-primary" 
                onClick={handleSaveSettings}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Export utility functions for use in other components
export const hasCookieConsent = (type = 'necessary') => {
  const consentCookie = getCookie('mycinemas_cookie_consent');
  if (!consentCookie) return type === 'necessary'; // Necessary is always allowed
  
  try {
    const consent = JSON.parse(consentCookie);
    return consent[type] === true;
  } catch (error) {
    return type === 'necessary';
  }
};

export const canUseLocalStorage = () => {
  return hasCookieConsent('preferences') || hasCookieConsent('necessary');
};

export default CookieConsent;

